import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, ArrowUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
  "Welcome to ADD VALUE. How may I help — the company, the collection, or an inquiry?";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(
    sessionStorage.getItem("av_chat") ||
      (() => {
        const id = crypto.randomUUID();
        sessionStorage.setItem("av_chat", id);
        return id;
      })()
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const appendToken = (token) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = {
        role: "assistant",
        text: copy[copy.length - 1].text + token,
      };
      return copy;
    });

  const setLastMessage = (text) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", text };
      return copy;
    });

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.t) appendToken(parsed.t);
            if (parsed.error)
              setLastMessage("The concierge is unavailable right now — please use the contact page.");
          } catch {
            // partial chunk, ignore
          }
        }
      }
    } catch {
      setLastMessage("The concierge is unavailable right now — please use the contact page.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        aria-label="Open concierge chat"
        className="fixed bottom-6 right-6 z-[90] flex h-14 w-14 items-center justify-center border border-[#2a2a2e] bg-[#121214] text-stone-200 shadow-2xl transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-[90] flex h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col border border-[#2a2a2e] bg-[#0d0d0f] shadow-2xl sm:right-6"
          >
            <div className="border-b border-[#222226] px-6 py-4">
              <p className="font-serif text-lg tracking-[0.1em] text-white">ADD VALUE</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
                Concierge
              </p>
            </div>

            <div data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                  <p
                    className={`inline-block max-w-[85%] px-4 py-3 text-sm font-light leading-relaxed ${
                      m.role === "user"
                        ? "bg-white text-black"
                        : "border border-[#222226] bg-[#121214] text-stone-300"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                  </p>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="flex gap-2 border-t border-[#222226] p-4">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about ADD VALUE…"
                maxLength={1000}
                className="w-full border border-[#2a2a2e] bg-[#0A0A0B] px-4 py-3 text-sm text-white placeholder:text-stone-600 focus:border-[#D4AF37] focus:outline-none"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="flex h-[46px] w-[46px] shrink-0 items-center justify-center bg-white text-black transition-colors duration-300 hover:bg-[#D4AF37] disabled:opacity-40"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
